import { BrowserSessionApiClient } from './browserSessionApiClient';

const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL || '/api').replace(/\/+$/, '');

export type ProjectDocumentCheckStatus = 'available' | 'incomplete' | 'missing' | 'ambiguous' | 'not_checked';

export type ProjectDocumentEvidence = {
  fileName: string;
  relativePath: string;
  extension: string;
  modifiedAt: string | null;
  role: string;
  itemId: string | null;
  webUrl: string | null;
};

export type ProjectDocumentCheck = {
  id: string;
  discipline: string;
  label: string;
  status: ProjectDocumentCheckStatus;
  summary: string;
  evidence: ProjectDocumentEvidence[];
  metadata: Record<string, string | number | boolean | null>;
};

export type ProjectDocumentStatusResult = {
  schemaVersion: 'pmapp.project-document-status.v2';
  project: {
    projectId: number;
    projectNumber: string;
    projectFolderName: string;
  };
  scan: {
    status: 'complete' | 'incomplete';
    scannedAt: string;
    source: 'sharepoint';
    filesObserved: number;
    truncated: boolean;
  };
  summary: Record<ProjectDocumentCheckStatus, number>;
  checks: ProjectDocumentCheck[];
  deliveryVerification: {
    status: 'not_checked';
    summary: string;
  };
  warnings: string[];
};

export type LocalDisplayTime = {
  value: string;
  label: string;
};

export type ProjectDocumentStatusResponse = {
  result: ProjectDocumentStatusResult;
  cache: {
    state: 'fresh' | 'refreshed' | 'stale';
    maxAgeHours: number;
    scannedAt: LocalDisplayTime | null;
    expiresAt: LocalDisplayTime | null;
    sharePointFolderUrl: string | null;
  };
  refreshError: string | null;
};

function unwrapData<T>(value: any): T {
  return (value?.data ?? value) as T;
}

class ProjectDocumentStatusService {
  private readonly browserClient = new BrowserSessionApiClient(API_BASE_URL);

  async getLatest(projectId: number): Promise<ProjectDocumentStatusResponse> {
    const response = await this.browserClient.request(`/api/v1/projects/${Math.trunc(projectId)}/document-status`);
    return unwrapData<ProjectDocumentStatusResponse>(response);
  }

  async refresh(projectId: number): Promise<ProjectDocumentStatusResponse> {
    const response = await this.browserClient.request(`/api/v1/projects/${Math.trunc(projectId)}/document-status/refresh`, {
      method: 'POST',
    });
    return unwrapData<ProjectDocumentStatusResponse>(response);
  }
}

export const projectDocumentStatusService = new ProjectDocumentStatusService();
